import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductLine, ShopQueueItem, ShopSummary, ShopToday } from '@popcart/core/v1';
import { api, errorMessage } from '../api';
import { ConfirmDialog, ExpressChip, LineDot, Loading, arrowNav, useNow, useTitle } from '../ui/bits';
import { Fig, Ring, Stat } from '../ui/viz';
import { Scene, SceneError } from '../catalog/art';
import { Icon } from '../ui/Icon';
import { usePrefs } from '../ui/prefs';
import { useToast } from '../ui/toast';
import { clock, count, countdown, minutes, paymentLabel, peso } from '../lib/format.js';
import { LINES, STATIONS, STATUS_VIEW, canCancel, filterByLine, itemSummary, itemsByStation, lineCounts, nextAction, oldestWaiting } from '../lib/queue.js';
import { sortStock, stockLevel } from '../lib/stock.js';
import { shopKeys, useShop } from './ShopLayout';
import { LineDots, StatusSticker, waitText } from './StatusSticker';

type Tab = ProductLine | 'all';
const TABS: Tab[] = ['all', 'fresh', 'grill', 'mart'];
const rise = (i: number) => ({ ['--i' as string]: i }) as CSSProperties;

function isTab(v: string | null): v is Tab {
  return v === 'all' || v === 'fresh' || v === 'grill' || v === 'mart';
}

/** Avg prep against the target: a full ring is "at target"; over it turns amber, then red. */
function prepRing(avg: number | null, target: number) {
  if (avg === null || !target) return { pct: null, tone: 'good' as const };
  const r = avg / target;
  return { pct: Math.min(100, r * 100), tone: r <= 1 ? ('good' as const) : r <= 1.25 ? ('warn' as const) : ('bad' as const) };
}

export function TodayPage() {
  const { shopId, base, today, todayQuery, queue, queueQuery, newIds } = useShop();
  useTitle(today ? `Today · ${today.shop.name}` : 'Today');
  const [params, setParams] = useSearchParams();
  const tab: Tab = isTab(params.get('line')) ? (params.get('line') as Tab) : 'all';
  const setTab = (t: Tab) => {
    const p = new URLSearchParams(params);
    if (t === 'all') p.delete('line');
    else p.set('line', t);
    setParams(p, { replace: true });
  };

  // Today shows the work still in the shop: new → ready. Picked up / done live on Orders.
  const active = useMemo(() => queue.filter((q) => q.status !== 'picked_up' && q.status !== 'delivered' && q.status !== 'cancelled'), [queue]);
  const counts = lineCounts(active);
  const visible = filterByLine(active, tab);
  const wanted = params.get('order');
  const [selectedId, setSelectedId] = useState<string | null>(wanted);
  useEffect(() => {
    if (wanted) setSelectedId(wanted);
  }, [wanted]);
  const selected = visible.find((q) => q.shopOrderId === selectedId) ?? visible[0] ?? null;

  const k = today?.kpis;
  const shop = today?.shop;
  const oldest = oldestWaiting(active);
  const waiting = active.filter((q) => q.status !== 'ready').length;
  const late = active.filter((q) => q.late).length;
  const ring = prepRing(k?.avgPrepMinutes ?? null, shop?.prepMinutes ?? 0);

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Today</h1>
          <span className="dv-live">
            <span className={`dv-pulse${shop?.open && shop.acceptingOrders ? '' : ' off ph-pulse-off'}`} aria-hidden="true" />
            {shop ? `${shop.open ? 'Open' : 'Closed'} · prep ${shop.prepMinutes} min · min ${peso(shop.minOrderCentavos)}` : ' '}
          </span>
        </div>
        <div className="dv-actions">
          <SoundToggle />
          {shop ? <TakingOrders shopId={shopId} shop={shop} /> : null}
        </div>
      </header>

      {todayQuery.isError ? (
        <section className="card">
          <SceneError error={todayQuery.error} onRetry={() => void todayQuery.refetch()} />
        </section>
      ) : !k ? (
        <div className="dv-stats">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="skeleton" style={{ minHeight: 132 }} aria-hidden="true" />
          ))}
        </div>
      ) : (
        <div className="dv-stats ph-stats dv-rise" style={rise(0)}>
          <Stat tone="ink" label="Sales today" value={peso(k.salesCentavos)} sub={k.orders ? `avg ${peso(Math.round(k.salesCentavos / k.orders))}` : 'No orders yet'} />
          <Stat label="Orders" value={count(k.orders)} sub={`${k.inProgress} in progress`} visual={<LineBars counts={counts} />} />
          <Stat label="Avg prep" value={minutes(k.avgPrepMinutes)} sub={`target ${shop?.prepMinutes ?? '—'} min`} visual={<Ring pct={ring.pct} tone={ring.tone} />} />
          <Stat
            label="Waiting"
            value={count(waiting)}
            tone={late ? 'alert' : undefined}
            sub={late ? <span className="dv-chip bad">{late} late</span> : oldest === null ? 'All caught up' : `oldest ${waitText(oldest)}`}
          />
        </div>
      )}

      <div className="ph-queue">
        <section className="card dv-card ph-qcard dv-rise" style={rise(1)} aria-labelledby="queue-h">
          <div className="dv-card-h">
            <h2 id="queue-h">Queue</h2>
            <span className="dv-chip" title="Express on top, then oldest">
              {counts.all}
            </span>
            {late ? <span className="dv-chip bad">{late} late</span> : null}
          </div>
          <div className="seg ph-seg" role="tablist" aria-label="Station" onKeyDown={(e) => arrowNav(e, '[role="tab"]')}>
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                id={`tab-${t}`}
                aria-selected={tab === t}
                aria-controls="queue-panel"
                tabIndex={tab === t ? 0 : -1}
                title={t === 'all' ? 'Every station' : STATIONS[t].station}
                onClick={() => setTab(t)}
              >
                {t === 'all' ? null : <LineDot line={t} />}
                {t === 'all' ? 'All' : STATIONS[t].tab} <b className="n">{counts[t]}</b>
              </button>
            ))}
          </div>
          <div id="queue-panel" role="tabpanel" aria-labelledby={`tab-${tab}`} className="ph-qlist">
            {queueQuery.isError ? (
              <SceneError error={queueQuery.error} onRetry={() => void queueQuery.refetch()} compact />
            ) : queueQuery.isLoading ? (
              <Loading label="Loading the queue" rows={3} />
            ) : visible.length ? (
              <ul aria-label="Orders">
                {visible.map((q) => (
                  <li key={q.shopOrderId}>
                    <QueueTicket item={q} selected={selected?.shopOrderId === q.shopOrderId} isNew={newIds.has(q.shopOrderId)} onSelect={() => setSelectedId(q.shopOrderId)} />
                  </li>
                ))}
              </ul>
            ) : (
              <Scene scene="noOrders" compact title={tab === 'all' ? 'All quiet for now' : `Nothing for ${STATIONS[tab].line} right now`}>
                New orders pop up here with a sound.
              </Scene>
            )}
          </div>
        </section>

        {selected ? (
          <OrderPanel key={selected.shopOrderId} shopId={shopId} item={selected} />
        ) : (
          <section className="card dv-card ph-order dv-rise" style={rise(2)} aria-label="Order">
            <Scene scene="noTickets" compact title="Pick an order">
              Tap a ticket to see what to pack.
            </Scene>
          </section>
        )}
      </div>

      <div className="ph-trio">
        <RunningLow shopId={shopId} soldOut={k?.soldOut ?? null} />
        <section className="card dv-card dv-rise" style={rise(4)} aria-labelledby="rating-h">
          <div className="dv-card-h">
            <h2 id="rating-h">Rating</h2>
            <span className="grow" />
            <Link to={`${base}/reviews`} className="dv-link">
              Reviews <Icon name="chevronRight" />
            </Link>
          </div>
          <div className="ph-rating">
            <Fig text={k?.rating == null ? '—' : k.rating.toFixed(1)} size="xl" />
            <Stars value={k?.rating ?? null} />
          </div>
        </section>
        <section className="card dv-card dv-rise" style={rise(5)} aria-labelledby="catch-h">
          <div className="dv-card-h">
            <h2 id="catch-h">Today's catch</h2>
          </div>
          <div className="ph-catch">
            <span className="dv-disc" aria-hidden="true">
              <Icon name="scale" />
            </span>
            <span className="dv-sub" title='Customers see "Arrived 5:40" on those products.'>
              Arrival time shows on products
            </span>
          </div>
          <Link className="btn btn-quiet" to={`${base}/fresh?tab=arrivals`} style={{ alignSelf: 'flex-start' }}>
            <Icon name="plus" size="sm" />
            Log an arrival
          </Link>
        </section>
      </div>
    </>
  );
}

/** Open orders per station as three short category bars (the Orders tile's visual). */
function LineBars({ counts }: { counts: Record<Tab, number> }) {
  const max = Math.max(1, ...LINES.map((l) => counts[l]));
  return (
    <span className="ph-linebars" aria-hidden="true">
      {LINES.map((l, j) => (
        <span key={l} title={`${STATIONS[l].line}: ${counts[l]}`}>
          <i className={`sw-${l}`} style={{ height: `${Math.max(6, (counts[l] / max) * 100)}%`, ['--j' as string]: j } as CSSProperties} />
          <small>{counts[l]}</small>
        </span>
      ))}
    </span>
  );
}

/** Five rating dots, filled to the rating (rounded to the nearest half). */
export function Stars({ value, size }: { value: number | null; size?: 'sm' }) {
  const v = value === null ? 0 : Math.round(value * 2) / 2;
  return (
    <span className={`ph-stars${size === 'sm' ? ' sm' : ''}`} aria-hidden="true">
      {Array.from({ length: 5 }, (_, i) => (
        <i key={i} className={v >= i + 1 ? 'on' : v >= i + 0.5 ? 'half' : ''} />
      ))}
    </span>
  );
}

function SoundToggle() {
  const { muted, setMuted } = usePrefs();
  return (
    <button type="button" className="dv-ibtn press" aria-pressed={!muted} aria-label={`Sound ${muted ? 'off' : 'on'}`} title={`New-order sound ${muted ? 'off' : 'on'}`} onClick={() => setMuted(!muted)}>
      <Icon name={muted ? 'bellOff' : 'bell'} />
    </button>
  );
}

function TakingOrders({ shopId, shop }: { shopId: string; shop: ShopSummary }) {
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: (on: boolean) => api.setAcceptingOrders(shopId, on),
    onMutate: async (on) => {
      await qc.cancelQueries({ queryKey: shopKeys.today(shopId) });
      const prev = qc.getQueryData<ShopToday>(shopKeys.today(shopId));
      qc.setQueryData<ShopToday>(shopKeys.today(shopId), (t) => (t ? { ...t, shop: { ...t.shop, acceptingOrders: on } } : t));
      return { prev };
    },
    onError: (err, _on, ctx) => {
      if (ctx?.prev) qc.setQueryData(shopKeys.today(shopId), ctx.prev);
      toast(errorMessage(err, "Couldn't change that. Try again."), 'error');
    },
    onSuccess: (s, on) => {
      qc.setQueryData<ShopToday>(shopKeys.today(shopId), (t) => (t ? { ...t, shop: { ...t.shop, ...s } } : t));
      toast(on ? 'Taking orders again.' : 'Paused. Customers can’t order until you switch it back on.');
    },
  });
  const on = shop.acceptingOrders;
  return (
    <button
      type="button"
      role="switch"
      className={`ph-open press${on ? ' on' : ''}`}
      aria-checked={on}
      aria-label="Taking orders"
      title={on ? "Pause if you're swamped" : 'Paused · customers can’t order'}
      disabled={m.isPending}
      onClick={() => m.mutate(!on)}
    >
      <span className="track" aria-hidden="true" />
      <b>{on ? 'Taking orders' : 'Paused'}</b>
    </button>
  );
}

function QueueTicket({ item, selected, isNew, onSelect }: { item: ShopQueueItem; selected: boolean; isNew: boolean; onSelect(): void }) {
  const now = useNow(1000);
  const st = STATUS_VIEW[item.status];
  const left = item.expressDeadline ? new Date(item.expressDeadline).getTime() - now : null;
  const cls = ['ph-ticket', item.late ? 'late' : '', isNew ? 'new' : ''].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} aria-current={selected ? 'true' : undefined} onClick={onSelect}>
      <span className="ph-tk-main">
        <span className="ph-tk-top">
          <span className="code">{item.code}</span>
          <LineDots lines={item.lines} />
          {item.speed === 'express' ? <ExpressChip>{left !== null && left > 0 ? countdown(left) : 'due'}</ExpressChip> : null}
        </span>
        <span className="ph-tk-items">{itemSummary(item)}</span>
      </span>
      <span className="ph-tk-side">
        <Fig text={waitText(item.minutesWaiting)} size="sm" animate={false} className={item.late ? 'ph-late' : undefined} />
        {item.late ? <span className="sr-only">Late</span> : null}
        <StatusSticker tone={st.tone}>{isNew && item.status === 'placed' ? 'New' : st.label}</StatusSticker>
      </span>
    </button>
  );
}

function OrderPanel({ shopId, item }: { shopId: string; item: ShopQueueItem }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const next = nextAction(item.status);
  const groups = itemsByStation(item);
  const now = useNow(1000);
  const left = item.expressDeadline ? new Date(item.expressDeadline).getTime() - now : null;
  const units = item.items.reduce((s, x) => s + x.qty, 0);

  const replace = (fresh: ShopQueueItem) =>
    qc.setQueryData<ShopQueueItem[]>(shopKeys.queue(shopId), (xs) => xs?.map((x) => (x.shopOrderId === fresh.shopOrderId ? fresh : x)));

  const advance = useMutation({
    mutationFn: (to: 'accepted' | 'preparing' | 'ready') => api.advanceShopOrder(shopId, item.shopOrderId, to),
    onSuccess: (fresh, to) => {
      if (fresh) replace(fresh);
      toast(to === 'accepted' ? `${item.code} accepted.` : to === 'preparing' ? `${item.code}: preparing.` : `${item.code} is ready for the rider.`);
      void qc.invalidateQueries({ queryKey: ['shop', shopId] });
    },
    onError: (err) => {
      toast(errorMessage(err, "Couldn't update the order. Try again."), 'error');
      void qc.invalidateQueries({ queryKey: shopKeys.queue(shopId) });
    },
  });
  const cancel = useMutation({
    mutationFn: (reason: string) => api.cancelShopOrder(shopId, item.shopOrderId, reason),
    onSuccess: (fresh) => {
      if (fresh) replace(fresh);
      setCancelOpen(false);
      toast(`${item.code} cancelled. The customer has been told.`);
      void qc.invalidateQueries({ queryKey: ['shop', shopId] });
    },
    onError: (err) => toast(errorMessage(err, "Couldn't cancel. Try again."), 'error'),
  });

  return (
    <section className={`card dv-card ph-order dv-rise${item.late ? ' late' : ''}`} style={rise(2)} aria-labelledby="order-h">
      <div className="ph-order-h">
        <div className="stack s4" style={{ minWidth: 0 }}>
          <h2 id="order-h">
            <span className="code">{item.code}</span> · {item.customerFirstName}
          </h2>
          <span className="dv-sub">
            {item.addressShort} · {paymentLabel(item.payment)} · {clock(item.placedAt)}
          </span>
        </div>
        <div className="ph-order-figs">
          <div>
            <span className="dv-label">Waiting</span>
            <Fig text={waitText(item.minutesWaiting)} size="md" className={item.late ? 'ph-late' : undefined} />
          </div>
          <div>
            <span className="dv-label">Items</span>
            <Fig text={count(units)} size="md" />
          </div>
        </div>
      </div>

      <div className="row wrap">
        {item.speed === 'express' ? <ExpressChip>Express · {left !== null && left > 0 ? `${countdown(left)} left` : 'due now'}</ExpressChip> : null}
        {item.late ? <span className="dv-chip bad">Running late</span> : null}
        <StatusSticker tone={STATUS_VIEW[item.status].tone}>{STATUS_VIEW[item.status].label}</StatusSticker>
        <span className="dv-chip" title={item.substitutionOk ? 'Customer said: substitute if something is sold out.' : 'Customer said: no substitutes. Leave out anything sold out.'}>
          <Icon name={item.substitutionOk ? 'swap' : 'close'} size="sm" />
          {item.substitutionOk ? 'Subs OK' : 'No subs'}
        </span>
      </div>

      {item.note ? (
        <div className="ph-note">
          <span className="dv-label">Customer note</span>
          <p>{item.note}</p>
        </div>
      ) : null}

      <div className="ph-stations">
        {groups.map((g) => (
          <div key={g.line} className="ph-station">
            <div className="dv-label row">
              <LineDot line={g.line} />
              {g.title}
            </div>
            <ul>
              {g.items.map((it) => {
                const key = `${it.productId}`;
                const meta = [it.unitLabel, it.shelf ? `Shelf ${it.shelf}` : null].filter(Boolean).join(' · ');
                const body = (
                  <span className="ph-line-nm">
                    <b>{it.name}</b>
                    {meta ? <small>{meta}</small> : null}
                  </span>
                );
                return (
                  <li key={key} className="ph-line">
                    <Fig text={`×${it.qty}`} size="md" animate={false} className="ph-qty" />
                    {g.line === 'mart' ? (
                      <label className="check-label">
                        <input
                          type="checkbox"
                          className="check"
                          checked={picked.has(key)}
                          onChange={(e) =>
                            setPicked((s) => {
                              const n = new Set(s);
                              if (e.target.checked) n.add(key);
                              else n.delete(key);
                              return n;
                            })
                          }
                        />
                        {body}
                      </label>
                    ) : (
                      body
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className="ph-order-foot">
        <span className="dv-sub" title={item.status === 'ready' ? 'Ops is booking the rider.' : 'The rider is booked when the order is ready, so food never waits.'}>
          <Icon name={item.status === 'ready' ? 'check' : 'pin'} size="sm" />
          {item.status === 'ready' ? 'Packed · rider on the way' : 'Rider booked at ready'}
        </span>
        <span className="row wrap">
          {canCancel(item.status) ? (
            <button type="button" className="btn btn-quiet" onClick={() => setCancelOpen(true)}>
              Can't fill it
            </button>
          ) : null}
          {next ? (
            <button type="button" className="btn btn-primary btn-lg" disabled={advance.isPending} aria-busy={advance.isPending || undefined} onClick={() => advance.mutate(next.to)}>
              {advance.isPending ? 'Saving…' : next.label}
            </button>
          ) : null}
        </span>
      </div>

      <ConfirmDialog
        open={cancelOpen}
        title={`Cancel ${item.code}?`}
        body="Only cancel if you really can't fill this order. The customer is refunded and told why."
        confirmLabel="Cancel order"
        reasonLabel="Reason (the customer sees this)"
        busy={cancel.isPending}
        onConfirm={(reason) => cancel.mutate(reason)}
        onClose={() => setCancelOpen(false)}
      />
    </section>
  );
}

function RunningLow({ shopId, soldOut }: { shopId: string; soldOut: number | null }) {
  const { base } = useShop();
  const low = useQuery({ queryKey: shopKeys.stock(shopId, true), queryFn: () => api.shopStock(shopId, true), refetchInterval: 30_000 });
  const all = low.data ? sortStock(low.data) : [];
  const rows = all.slice(0, 5);
  const max = Math.max(1, ...rows.map((r) => r.onHand || r.available));
  return (
    <section className="card dv-card dv-rise" style={rise(3)} aria-labelledby="low-h">
      <div className="dv-card-h">
        <h2 id="low-h">Running low</h2>
        {low.data ? <span className="dv-chip">{all.length}</span> : null}
        {soldOut ? <span className="dv-chip bad">{soldOut} out</span> : null}
        <span className="grow" />
        <Link to={`${base}/products`} className="dv-link">
          Stock <Icon name="chevronRight" />
        </Link>
      </div>
      {low.isError ? (
        <SceneError error={low.error} onRetry={() => void low.refetch()} compact />
      ) : !low.data ? (
        <Loading label="Loading stock" />
      ) : rows.length ? (
        <ul className="ph-low">
          {rows.map((r) => {
            const out = stockLevel(r) === 'sold_out';
            return (
              <li key={r.productId} className={out ? 'out' : ''}>
                <LineDot line={r.line} />
                <span className="nm">{r.name}</span>
                <span className="dv-share" aria-hidden="true">
                  <i style={{ width: `${Math.max(4, (r.available / max) * 100)}%` }} />
                </span>
                {out ? <span className="dv-chip bad">Out</span> : <Fig text={count(r.available)} size="xs" animate={false} />}
                <span className="sr-only">{out ? 'Sold out' : 'Low'}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="dv-quiet">
          <Icon name="check" />
          Shelves look full
        </p>
      )}
    </section>
  );
}
